import { ApplicationController } from "../support/application-controller";
import { fetchPatch, fetchPost, fetchDelete } from "../support/api-helpers";

export default class extends ApplicationController {
  static targets = ["list"];

  submitExistingRow(event) {
    const mappingCard = event.target.closest("[data-external-mapping-id]");
    const id = mappingCard.dataset.externalMappingId;
    const external_source_type = mappingCard.querySelector('[data-field="external_source_type"]').value.trim();
    const external_source_id = mappingCard.querySelector('[data-field="external_source_id"]').value.trim();

    if (!external_source_type || !external_source_id) {
      this.showAlert("All fields are required.", "error");
      return;
    }

    // Show loading state
    const saveButton = event.target;
    const originalText = saveButton.innerHTML;
    saveButton.innerHTML = '⏳ Saving...';
    saveButton.disabled = true;

    fetchPatch({
      url: `/admin/external_mappings/${id}`,
      data: { external_mapping: { external_source_type, external_source_id } },
    }).then(() => {
      this.showAlert("External mapping updated successfully!", "success");
      setTimeout(() => window.location.reload(), 1000);
    })
    .catch((err) => {
      console.error('Update failed:', err);
      this.showAlert(`Update failed: ${err.message}`, "error");
      saveButton.innerHTML = originalText;
      saveButton.disabled = false;
    });
  }

  destroy(event) {
    const mappingCard = event.target.closest("[data-external-mapping-id]");
    const id = mappingCard.dataset.externalMappingId;

    // Show loading state
    const deleteButton = event.target;
    const originalText = deleteButton.innerHTML;
    deleteButton.innerHTML = '⏳ Deleting...';
    deleteButton.disabled = true;

    fetchDelete({ url: `/admin/external_mappings/${id}` })
      .then(() => {
        this.showAlert("External mapping deleted successfully!", "success");
        setTimeout(() => window.location.reload(), 1000);
      })
      .catch((err) => {
        console.error('Delete failed:', err);
        this.showAlert(`Delete failed: ${err.message}`, "error");
        deleteButton.innerHTML = originalText;
        deleteButton.disabled = false;
      });
  }

  quickAddMapping(event) {
    const sourceType = event.target.dataset.sourceType || event.target.closest('button').dataset.sourceType;
    const localSourceId = this.element.dataset.localSourceId;

    // Show the form
    this.showNewMappingForm();

    // Pre-populate the source type
    const sourceTypeSelect = document.getElementById(`new-external-source-type-${localSourceId}`);
    if (sourceTypeSelect) {
      sourceTypeSelect.value = sourceType;
      sourceTypeSelect.dispatchEvent(new Event('change'));
    }

    // Focus on the ID input
    const idInput = document.getElementById(`new-external-source-id-${localSourceId}`);
    if (idInput) {
      setTimeout(() => idInput.focus(), 100);
    }
  }

  showNewMappingForm() {
    const localSourceId = this.element.dataset.localSourceId;
    const form = document.getElementById(`new-mapping-form-${localSourceId}`);
    if (form) {
      form.style.display = 'block';
      form.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  cancelNewMapping() {
    const localSourceId = this.element.dataset.localSourceId;
    const form = document.getElementById(`new-mapping-form-${localSourceId}`);
    if (form) {
      form.style.display = 'none';

      // Clear form fields
      const sourceTypeSelect = document.getElementById(`new-external-source-type-${localSourceId}`);
      const idInput = document.getElementById(`new-external-source-id-${localSourceId}`);
      if (sourceTypeSelect) sourceTypeSelect.value = '';
      if (idInput) idInput.value = '';
    }
  }

  saveNewMapping() {
    const localSourceId = this.element.dataset.localSourceId;
    const external_source_type = document.getElementById(`new-external-source-type-${localSourceId}`).value.trim();
    const external_source_id = document.getElementById(`new-external-source-id-${localSourceId}`).value.trim();
    const local_source_type = this.element.dataset.localSourceType;

    if (!external_source_type || !external_source_id) {
      this.showAlert("All fields are required.", "error");
      return;
    }

    if (!local_source_type || !localSourceId) {
      this.showAlert("Local source type and ID are missing from the controller element's data attributes.", "error");
      return;
    }

    // Show loading state
    const saveButton = event.target;
    const originalText = saveButton.innerHTML;
    saveButton.innerHTML = '⏳ Creating...';
    saveButton.disabled = true;

    fetchPost({
      url: `/admin/external_mappings`,
      data: { external_mapping: { external_source_type, external_source_id, local_source_type, local_source_id: localSourceId } }
    }).then(() => {
      this.showAlert("External mapping created successfully!", "success");
      setTimeout(() => window.location.reload(), 1000);
    })
    .catch((err) => {
      console.error('Create failed:', err);

      // Check if it's a 422 response with existing_mapping_id
      if (err.status === 422 && err.data && err.data.existing_mapping_id) {
        const existingMappingId = err.data.existing_mapping_id;
        const errorMessage = err.data.errors && err.data.errors.length > 0 ? err.data.errors[0] : 'This mapping already exists';

        // Create a custom confirmation dialog
        this.showConfirmationDialog(
          'Mapping Conflict',
          `${errorMessage}. Would you like to transfer this existing mapping to the current record?`,
          'Transfer Mapping',
          'Cancel',
          () => this.transferExistingMapping(existingMappingId, localSourceId),
          () => {
            saveButton.innerHTML = originalText;
            saveButton.disabled = false;
          }
        );
      } else {
        this.showAlert(`Create failed: ${err.message}`, "error");
        saveButton.innerHTML = originalText;
        saveButton.disabled = false;
      }
    });
  }

  // Helper method to show styled alerts
  showAlert(message, type) {
    const alertDiv = document.createElement('div');
    const bgColor = type === 'success' ? '#d4edda' : '#f8d7da';
    const textColor = type === 'success' ? '#155724' : '#721c24';
    const borderColor = type === 'success' ? '#c3e6cb' : '#f5c6cb';
    const icon = type === 'success' ? '✅' : '⚠️';

    alertDiv.innerHTML = `
      <div style="position:fixed;top:20px;right:20px;z-index:10000;background:${bgColor};color:${textColor};border:1px solid ${borderColor};padding:12px 16px;border-radius:6px;box-shadow:0 4px 12px rgba(0,0,0,0.15);max-width:400px;">
        <div style="display:flex;align-items:center;">
          <span style="margin-right:8px;">${icon}</span>
          <span style="font-size:14px;font-weight:500;">${message}</span>
          <button onclick="this.parentElement.parentElement.remove()" style="margin-left:auto;background:none;border:none;color:${textColor};cursor:pointer;padding:0 4px;">✕</button>
        </div>
      </div>
    `;

    document.body.appendChild(alertDiv);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (alertDiv.parentElement) {
        alertDiv.remove();
      }
    }, 5000);
  }

  // Transfers an existing mapping to current subscription
  transferExistingMapping(existingMappingId, targetSubscriptionId) {
    fetchPost({
      url: `/admin/external_mappings/${existingMappingId}/transfer_subscription_mapping`,
      data: { target_subscription_id: targetSubscriptionId }
    }).then(() => {
      this.showAlert("External mapping transferred successfully!", "success");
      setTimeout(() => window.location.reload(), 1000);
    })
    .catch((err) => {
      console.error('Transfer failed:', err);
      this.showAlert(`Transfer failed: ${err.message}`, "error");
    });
  }

  // Shows a confirmation dialog with action buttons
  showConfirmationDialog(title, message, confirmText, cancelText, onConfirm, onCancel) {
    // Create modal container
    const modalDiv = document.createElement('div');
    modalDiv.style.position = 'fixed';
    modalDiv.style.top = '0';
    modalDiv.style.left = '0';
    modalDiv.style.right = '0';
    modalDiv.style.bottom = '0';
    modalDiv.style.backgroundColor = 'rgba(0,0,0,0.5)';
    modalDiv.style.zIndex = '10001';
    modalDiv.style.display = 'flex';
    modalDiv.style.alignItems = 'center';
    modalDiv.style.justifyContent = 'center';

    // Create modal content
    modalDiv.innerHTML = `
      <div style="background:white;border-radius:8px;max-width:500px;width:90%;padding:24px;box-shadow:0 4px 16px rgba(0,0,0,0.2);">
        <h3 style="margin-top:0;color:#333;font-size:18px;font-weight:600;">${title}</h3>
        <p style="margin-bottom:24px;color:#555;font-size:14px;">${message}</p>
        <div style="display:flex;justify-content:flex-end;gap:12px;">
          <button id="modal-cancel-btn" style="padding:8px 16px;background:none;border:1px solid #ccc;border-radius:4px;cursor:pointer;">${cancelText}</button>
          <button id="modal-confirm-btn" style="padding:8px 16px;background:#007bff;color:white;border:none;border-radius:4px;cursor:pointer;">${confirmText}</button>
        </div>
      </div>
    `;

    document.body.appendChild(modalDiv);

    // Add event listeners
    document.getElementById('modal-confirm-btn').addEventListener('click', () => {
      modalDiv.remove();
      if (onConfirm) onConfirm();
    });

    document.getElementById('modal-cancel-btn').addEventListener('click', () => {
      modalDiv.remove();
      if (onCancel) onCancel();
    });
  }

  // Legacy methods for backward compatibility
  addRow() {
    this.addCustomMapping();
  }

  submitNewRow(event) {
    this.saveNewMapping(event);
  }

  removeNewRow(event) {
    this.cancelNewMapping(event);
  }
}
